import { StatusCell } from '@/features/settings/marking/documents/StatusCell'
import {
    EditPencilIcon,
    MinusRoundSVGIcon,
    PlusRoundSVGIcon,
    TrashSVGIcon,
} from '@/shared/assets'
import { Button, DisableTooltip, ShadcnButton } from '@/shared/ui'
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/shared/ui/collapsible'
import {
    VkOrdAgencyActStatus,
    VkOrdAgencyContractStatus,
    VkOrdAgencyContractType,
} from '@synapex/prisma/client'
import { PlusIcon } from 'lucide-react'
import { useState } from 'react'
import { AgencyContractTableRow } from '../types'

const getRuContractTypeName = (type: VkOrdAgencyContractType) => {
    switch (type) {
        case 'mediation':
            return 'Посреднический'
        case 'service':
            return 'Услуговый'
        default:
            return 'Услуговый'
    }
}

interface FrameworkContractRowProps {
    contract: AgencyContractTableRow
    canDeleteContract: boolean
    // canCreateAct: boolean
    canEditContract: boolean
    onEditContract: (contractId: number) => void
    onDeleteContract: (contractId: number) => void
    onAddAct: (contractId: number) => void
    onEditAct: (actId: number) => void
    onDeleteAct: (actId: number) => void
    onUpdateContractStatus: (
        status: VkOrdAgencyContractStatus,
        contractId: number
    ) => void
    onUpdateActStatus: (status: VkOrdAgencyActStatus, actId: number) => void
}

export const FrameworkContractRow = ({
    contract,
    canDeleteContract,
    // canCreateAct,
    canEditContract,
    onEditContract,
    onDeleteContract,
    onAddAct,
    onEditAct,
    onDeleteAct,
    onUpdateContractStatus,
    onUpdateActStatus,
}: FrameworkContractRowProps) => {
    const [isOpen, setIsOpen] = useState(false)

    return (
        <Collapsible
            key={`contract-${contract.contractId}`}
            asChild
            open={isOpen}
            onOpenChange={setIsOpen}
        >
            <>
                <tr className="">
                    <td className="!relative !w-20 !min-w-fit pr-2 !text-start !align-middle">
                        <div className="flex items-center justify-between">
                            <span className="whitespace-nowrap">
                                {new Date(
                                    contract.contractStartDate
                                ).toLocaleDateString('ru-RU', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: '2-digit',
                                })}
                            </span>
                            {contract.acts.length > 0 && (
                                <CollapsibleTrigger asChild>
                                    <div className="relative h-5 w-5 shrink-0 cursor-pointer">
                                        <PlusRoundSVGIcon
                                            className={`absolute h-5 w-5 transition-all duration-300 ${
                                                isOpen
                                                    ? 'rotate-90 opacity-0'
                                                    : 'rotate-0 opacity-100'
                                            }`}
                                        />
                                        <MinusRoundSVGIcon
                                            className={`absolute h-5 w-5 transition-all duration-300 ${
                                                isOpen
                                                    ? 'rotate-0 opacity-100'
                                                    : '-rotate-90 opacity-0'
                                            }`}
                                        />
                                    </div>
                                </CollapsibleTrigger>
                            )}
                        </div>
                    </td>
                    <td className="!relative !w-20 !min-w-fit pr-2 !text-start !align-middle">
                        {getRuContractTypeName(contract.type)}
                    </td>
                    <td className="!w-20 !min-w-fit pr-2 !text-start !align-middle">
                        {contract.contractNumber}
                    </td>
                    <td className="flex items-center gap-2 pr-2 !text-start !align-middle">
                        <StatusCell
                            onChange={(value) =>
                                onUpdateContractStatus(
                                    value as VkOrdAgencyContractStatus,
                                    contract.contractId
                                )
                            }
                            value={contract.status}
                            documentType="contract"
                            className="w-20"
                        />
                        <DisableTooltip
                            enabled={!canEditContract}
                            message={
                                'Нельзя редактировать договор когда есть акты'
                            }
                            className="flex items-center"
                        >
                            <ShadcnButton
                                variant="ghost"
                                onClick={() =>
                                    onEditContract(contract.contractId)
                                }
                                className="h-fit p-0"
                                disabled={!canEditContract}
                            >
                                <EditPencilIcon className="h-4 w-4" />
                            </ShadcnButton>
                        </DisableTooltip>
                        <DisableTooltip
                            enabled={!canDeleteContract}
                            message={
                                'Нельзя удалить договор который уже используется'
                            }
                            className="flex items-center"
                        >
                            <ShadcnButton
                                className="h-fit p-0"
                                variant="ghost"
                                disabled={!canDeleteContract}
                                onClick={() =>
                                    onDeleteContract(contract.contractId)
                                }
                            >
                                <TrashSVGIcon />
                            </ShadcnButton>
                        </DisableTooltip>
                    </td>
                    <td className="!w-20 pr-2 !text-start !align-middle">
                        {/* <DisableTooltip
                            enabled={!canCreateAct}
                            message={
                                'Нельзя сгенерировать акт пока договор не завершен и не созданы акты в связанных договорах'
                            }
                            className="flex items-center"
                        > */}
                        <Button
                            variant="secondary"
                            className="m-0 flex max-w-32 gap-1 text-xs font-semibold"
                            size="small"
                            // visuallyDisabled={!canCreateAct}
                            onClick={() => {
                                // if (canCreateAct) {
                                onAddAct(contract.contractId)
                                // }
                            }}
                        >
                            Добавить акт
                            <PlusIcon className="h-4 w-4 font-semibold" />
                        </Button>
                        {/* </DisableTooltip> */}
                    </td>
                    <td colSpan={6} />
                </tr>

                <CollapsibleContent asChild>
                    <>
                        {contract.acts.map((act, index) => {
                            const isLastAct = index === contract.acts.length - 1
                            return (
                                <tr key={`act-${act.actId}`}>
                                    <td className="!relative !w-20 !min-w-fit !border-none !bg-synapex-primary-bg pr-2 !text-start !align-middle">
                                        {act.actDate &&
                                            new Date(
                                                act.actDate
                                            ).toLocaleDateString('ru-RU', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: '2-digit',
                                            })}
                                    </td>
                                    <td className="!relative !w-20 !min-w-fit !border-none !bg-synapex-primary-bg !text-start !align-middle">
                                        {/* <span className="text-gray-400">
                                            {getRuContractTypeName(
                                                contract.type
                                            )}
                                        </span> */}
                                    </td>
                                    <td
                                        className={`relative !w-20 !border-none !bg-synapex-primary-bg p-0 !text-start !align-middle ${
                                            isLastAct
                                                ? '!border-l-0'
                                                : '!border-l !border-l-inherit'
                                        }`}
                                    >
                                        {isLastAct ? (
                                            <>
                                                <div className="absolute top-0 h-[25px] w-[1px] bg-stroke" />
                                                <div className="h-[1px] w-full bg-stroke" />
                                            </>
                                        ) : (
                                            <>
                                                <div className="absolute top-0 h-full w-[1px] bg-stroke" />
                                                <div className="h-[1px] w-full bg-stroke" />
                                            </>
                                        )}
                                    </td>
                                    <td className="!w-20 !border-none !bg-synapex-primary-bg p-0 !text-start !align-middle">
                                        <div className="h-[1px] w-full bg-stroke" />
                                    </td>
                                    {/* <td className="!w-40 !border-none !bg-synapex-primary-bg p-0 pr-2 !text-start !align-middle">
                                        <div className="h-[1px] w-full bg-stroke" />
                                    </td> */}
                                    <td className="!w-20 !max-w-20 overflow-hidden text-ellipsis !border-none !bg-synapex-primary-bg pr-2 !text-start !align-middle">
                                        {act.actNumber}
                                    </td>
                                    <td className="!w-20 !border-none !bg-synapex-primary-bg pr-2 !text-start !align-middle">
                                        {act.actStartDate &&
                                            new Date(
                                                act.actStartDate
                                            ).toLocaleDateString('ru-RU', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: '2-digit',
                                            })}
                                    </td>
                                    <td className="!w-20 !border-none !bg-synapex-primary-bg pr-2 !text-start !align-middle">
                                        {act.actEndDate &&
                                            new Date(
                                                act.actEndDate
                                            ).toLocaleDateString('ru-RU', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: '2-digit',
                                            })}
                                    </td>
                                    <td className="!w-20 !border-none !bg-synapex-primary-bg pr-2 !text-start !align-middle">
                                        {act.actBudgetInApplication
                                            ? act.actBudgetInApplication
                                            : null}
                                    </td>
                                    <td className="!w-20 !border-none !bg-synapex-primary-bg pr-2 !text-start !align-middle">
                                        {act.actBudget ? act.actBudget : null}
                                    </td>
                                    <td className="!w-20 !border-none !bg-synapex-primary-bg pr-2 !text-start !align-middle">
                                        {contract.type === 'mediation'
                                            ? act.actRemuneration
                                            : '-'}
                                    </td>
                                    <td className="flex items-center gap-2 !border-none !bg-synapex-primary-bg pr-2 !text-start !align-middle">
                                        <StatusCell
                                            onChange={(value) =>
                                                onUpdateActStatus(
                                                    value as VkOrdAgencyActStatus,
                                                    act.actId
                                                )
                                            }
                                            value={act.actStatus}
                                            documentType="act"
                                            className="w-32"
                                            actDate={act.actDate}
                                        />
                                        <DisableTooltip
                                            enabled={act.actStatus === 'formed'}
                                            message={
                                                'Акт уже загружен в ОРД и не может быть изменен или удален'
                                            }
                                        >
                                            <ShadcnButton
                                                variant="ghost"
                                                onClick={() =>
                                                    onEditAct(act.actId)
                                                }
                                                className="h-full p-0"
                                                disabled={
                                                    act.actStatus === 'formed'
                                                }
                                            >
                                                <EditPencilIcon className="h-4 w-4" />
                                            </ShadcnButton>
                                        </DisableTooltip>
                                        <DisableTooltip
                                            enabled={act.actStatus === 'formed'}
                                            message={
                                                'Акт уже загружен в ОРД и не может быть изменен или удален'
                                            }
                                        >
                                            <ShadcnButton
                                                className="h-fit p-0"
                                                variant="ghost"
                                                onClick={() =>
                                                    onDeleteAct(act.actId)
                                                }
                                                disabled={
                                                    act.actStatus === 'formed'
                                                }
                                            >
                                                <TrashSVGIcon />
                                            </ShadcnButton>
                                        </DisableTooltip>
                                    </td>
                                </tr>
                            )
                        })}
                    </>
                </CollapsibleContent>
            </>
        </Collapsible>
    )
}
